import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import {
  Package, Settings, RefreshCw, Download, Upload, Eye, EyeOff, Trash2, Edit, Save, AlertTriangle, CheckCircle, XCircle, Activity, BarChart3, Clock, Users, Zap, Shield, Database, X, Info
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';
import toast from 'react-hot-toast';

const InstalledPlugins = () => {
  const navigate = useNavigate();
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlugin, setSelectedPlugin] = useState(null);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDependencyModal, setShowDependencyModal] = useState(false);
  const [showUpdatesModal, setShowUpdatesModal] = useState(false);
  const [configData, setConfigData] = useState({});
  const [updateData, setUpdateData] = useState({});
  const [availableUpdates, setAvailableUpdates] = useState([]);
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortOrder, setSortOrder] = useState('name');

  useEffect(() => {
    const fetchInstalledPlugins = async () => {
      try {
        setLoading(true);
        const response = await MockDataService.getInstalledPlugins();
        if (response.success) {
          setPlugins(response.data);
        } else {
          setError(response.message || "Failed to fetch installed plugins.");
        }
      } catch (err) {
        setError("Failed to fetch installed plugins.");
        console.error("Error fetching installed plugins:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInstalledPlugins();
  }, []);

  const handleTogglePlugin = async (pluginId) => {
    try {
      const response = await MockDataService.togglePlugin(pluginId);
      if (response.success) {
        setPlugins(prevPlugins =>
          prevPlugins.map(plugin =>
            plugin.id === pluginId ? { ...plugin, enabled: !plugin.enabled } : plugin
          )
        );
        alert(`Plugin ${plugins.find(p => p.id === pluginId)?.enabled ? 'disabled' : 'enabled'} successfully!`);
      } else {
        alert('Failed to toggle plugin status');
      }
    } catch (err) {
      alert('Failed to toggle plugin status');
      console.error("Error toggling plugin:", err);
    }
  };

  const handleConfigurePlugin = (plugin) => {
    setSelectedPlugin(plugin);
    setConfigData(plugin.config || {});
    setShowConfigModal(true);
  };

  const handleSaveConfiguration = async () => {
    try {
      const response = await MockDataService.updatePluginConfiguration(selectedPlugin.id, configData);
      if (response.success) {
        setPlugins(prevPlugins =>
          prevPlugins.map(plugin =>
            plugin.id === selectedPlugin.id ? { ...plugin, config: configData } : plugin
          )
        );
        setShowConfigModal(false);
        alert('Plugin configuration saved successfully!');
      } else {
        alert('Failed to save plugin configuration');
      }
    } catch (err) {
      alert('Failed to save plugin configuration');
      console.error("Error saving configuration:", err);
    }
  };

  const handleUpdatePlugin = (plugin) => {
    setSelectedPlugin(plugin);
    setUpdateData({
      currentVersion: plugin.version,
      availableVersion: plugin.availableVersion,
      updateNotes: plugin.updateNotes || []
    });
    setShowUpdateModal(true);
  };

  const handlePerformUpdate = async () => {
    try {
      const response = await MockDataService.updatePlugin(selectedPlugin.id);
      if (response.success) {
        setPlugins(prevPlugins =>
          prevPlugins.map(plugin =>
            plugin.id === selectedPlugin.id ? { ...plugin, version: response.data.version, lastUpdated: new Date().toISOString() } : plugin
          )
        );
        setShowUpdateModal(false);
        alert('Plugin updated successfully!');
      } else {
        alert('Failed to update plugin');
      }
    } catch (err) {
      alert('Failed to update plugin');
      console.error("Error updating plugin:", err);
    }
  };

  const handleViewDependencies = (plugin) => {
    setSelectedPlugin(plugin);
    setShowDependencyModal(true);
  };

  const handleCheckUpdates = async () => {
    setCheckingUpdates(true);
    try {
      // Simulate checking for updates
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Find all plugins with updates available
      const updates = plugins
        .filter(plugin => plugin.hasUpdate)
        .map(plugin => ({
          id: plugin.id,
          name: plugin.name,
          currentVersion: plugin.version,
          availableVersion: plugin.availableVersion || `${parseFloat(plugin.version) + 0.1}`,
          updateNotes: plugin.updateNotes || [
            'Bug fixes and performance improvements',
            'New features added',
            'Security enhancements'
          ],
          category: plugin.category,
          description: plugin.description
        }));

      setAvailableUpdates(updates);
      
      if (updates.length > 0) {
        setShowUpdatesModal(true);
        toast.success(`Found ${updates.length} update${updates.length > 1 ? 's' : ''} available!`);
      } else {
        toast.success('All plugins are up to date!');
      }
    } catch (err) {
      toast.error('Failed to check for updates');
      console.error('Error checking updates:', err);
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleBulkUpdate = async () => {
    try {
      setCheckingUpdates(true);
      let successCount = 0;
      let failCount = 0;

      for (const update of availableUpdates) {
        try {
          const response = await MockDataService.updatePlugin(update.id);
          if (response.success) {
            setPlugins(prevPlugins =>
              prevPlugins.map(plugin =>
                plugin.id === update.id
                  ? {
                      ...plugin,
                      version: response.data.version,
                      availableVersion: undefined,
                      hasUpdate: false,
                      lastUpdated: new Date().toISOString()
                    }
                  : plugin
              )
            );
            successCount++;
          } else {
            failCount++;
          }
        } catch (err) {
          failCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`Successfully updated ${successCount} plugin${successCount > 1 ? 's' : ''}`);
      }
      if (failCount > 0) {
        toast.error(`Failed to update ${failCount} plugin${failCount > 1 ? 's' : ''}`);
      }

      setShowUpdatesModal(false);
      setAvailableUpdates([]);
    } catch (err) {
      toast.error('Failed to perform bulk update');
      console.error('Error performing bulk update:', err);
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleUninstallPlugin = async (pluginId) => {
    if (window.confirm('Are you sure you want to uninstall this plugin? This action cannot be undone.')) {
      try {
        const response = await MockDataService.uninstallPlugin(pluginId);
        if (response.success) {
          setPlugins(prevPlugins => prevPlugins.filter(plugin => plugin.id !== pluginId));
          alert('Plugin uninstalled successfully!');
        } else {
          alert('Failed to uninstall plugin');
        }
      } catch (err) {
        alert('Failed to uninstall plugin');
        console.error("Error uninstalling plugin:", err);
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'secondary';
      case 'error': return 'destructive';
      case 'updating': return 'outline';
      default: return 'outline';
    }
  };

  const getHealthColor = (health) => {
    if (health >= 90) return 'text-green-600';
    if (health >= 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  const filteredPlugins = plugins
    .filter(plugin => {
      const matchesSearch = plugin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            plugin.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = filterStatus === 'all' || 
                            (filterStatus === 'enabled' && plugin.enabled) ||
                            (filterStatus === 'disabled' && !plugin.enabled) ||
                            (filterStatus === 'error' && plugin.status === 'error');
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortOrder) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'version':
          return b.version.localeCompare(a.version);
        case 'health':
          return b.health - a.health;
        case 'installed':
          return new Date(b.installedAt) - new Date(a.installedAt);
        default:
          return a.name.localeCompare(b.name);
      }
    });

  if (loading) return <div className="p-6 text-center">Loading Installed Plugins...</div>;
  if (error) return <div className="p-6 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Installed Plugins</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage and configure your installed plugins.</p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={() => navigate('/admin/plugins/marketplace')}
            className="flex items-center gap-2"
          >
            <Package className="h-4 w-4" />
            Browse Store
          </Button>
          <Button 
            variant="outline"
            onClick={handleCheckUpdates}
            disabled={checkingUpdates}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${checkingUpdates ? 'animate-spin' : ''}`} />
            {checkingUpdates ? 'Checking...' : 'Check Updates'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Installed</CardTitle>
            <Package className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plugins.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active</CardTitle>
            <CheckCircle className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plugins.filter(p => p.enabled).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Updates Available</CardTitle>
            <Download className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plugins.filter(p => p.hasUpdate).length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Health</CardTitle>
            <Activity className="h-4 w-4 text-gray-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {plugins.length > 0 ? Math.round(plugins.reduce((sum, p) => sum + p.health, 0) / plugins.length) : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="relative flex-1 min-w-64">
              <Input
                type="text"
                placeholder="Search plugins..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="all">All Status</option>
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
              <option value="error">Error</option>
            </select>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="px-3 py-2 border rounded-md"
            >
              <option value="name">Sort by: Name</option>
              <option value="version">Sort by: Version</option>
              <option value="health">Sort by: Health</option>
              <option value="installed">Sort by: Installed Date</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Plugins List */}
      <div className="space-y-4">
        {filteredPlugins.map((plugin) => (
          <Card key={plugin.id}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    <h3 className="text-lg font-semibold">{plugin.name}</h3>
                    <Badge variant={getStatusColor(plugin.status)}>
                      {plugin.status}
                    </Badge>
                    <Badge variant={plugin.enabled ? 'success' : 'secondary'}>
                      {plugin.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                    {plugin.hasUpdate && (
                      <Badge variant="outline" className="text-orange-600 border-orange-600">
                        Update Available
                      </Badge>
                    )}
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-3">{plugin.description}</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>v{plugin.version}</span>
                    </div>
                    <div className="flex items-center">
                      <Activity className={`h-4 w-4 mr-1 ${getHealthColor(plugin.health)}`} />
                      <span className={getHealthColor(plugin.health)}>{plugin.health}% Health</span>
                    </div>
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-1" />
                      <span>{plugin.author}</span>
                    </div>
                    <div className="flex items-center">
                      <Database className="h-4 w-4 mr-1" />
                      <span>{plugin.category}</span>
                    </div>
                  </div>
                  {plugin.dependencies && plugin.dependencies.length > 0 && (
                    <div className="mt-3">
                      <span className="text-sm text-gray-500">Dependencies: </span>
                      <span className="text-sm text-blue-600">{plugin.dependencies.length} plugins</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col space-y-2 ml-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleConfigurePlugin(plugin)}
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Configure
                  </Button>
                  {plugin.hasUpdate && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdatePlugin(plugin)}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Update
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTogglePlugin(plugin.id)}
                  >
                    {plugin.enabled ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    {plugin.enabled ? 'Disable' : 'Enable'}
                  </Button>
                  {plugin.dependencies && plugin.dependencies.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDependencies(plugin)}
                    >
                      <Shield className="h-4 w-4 mr-1" />
                      Dependencies
                    </Button>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleUninstallPlugin(plugin.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Uninstall
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Configuration Modal */}
      <Modal isOpen={showConfigModal} onClose={() => setShowConfigModal(false)} className="max-w-2xl">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Configure {selectedPlugin?.name}</h2>
          {selectedPlugin && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Plugin Settings</label>
                <div className="space-y-3">
                  {Object.entries(configData).map(([key, value]) => (
                    <div key={key}>
                      <label className="block text-sm font-medium mb-1">{key}</label>
                      <Input
                        value={value}
                        onChange={(value) => setConfigData({...configData, [key]: value})}
                        placeholder={`Enter ${key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowConfigModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSaveConfiguration}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Configuration
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Update Modal */}
      <Modal isOpen={showUpdateModal} onClose={() => setShowUpdateModal(false)} className="max-w-lg">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Update {selectedPlugin?.name}</h2>
          {selectedPlugin && (
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-center space-x-2 mb-2">
                  <Download className="h-5 w-5 text-blue-600" />
                  <span className="font-medium">Update Available</span>
                </div>
                <p className="text-sm text-gray-600">
                  Update from v{updateData.currentVersion} to v{updateData.availableVersion}
                </p>
              </div>
              {updateData.updateNotes && updateData.updateNotes.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Update Notes:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {updateData.updateNotes.map((note, index) => (
                      <li key={index}>{note}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowUpdateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handlePerformUpdate}>
                  <Download className="h-4 w-4 mr-2" />
                  Update Plugin
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Dependencies Modal */}
      <Modal isOpen={showDependencyModal} onClose={() => setShowDependencyModal(false)} className="max-w-lg">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4">Plugin Dependencies</h2>
          {selectedPlugin && (
            <div className="space-y-4">
              <p className="text-gray-600">
                {selectedPlugin.name} depends on the following plugins:
              </p>
              <div className="space-y-2">
                {selectedPlugin.dependencies?.map((dep, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <div className="font-medium">{dep.name}</div>
                      <div className="text-sm text-gray-500">v{dep.version}</div>
                    </div>
                    <Badge variant={dep.installed ? 'success' : 'destructive'}>
                      {dep.installed ? 'Installed' : 'Missing'}
                    </Badge>
                  </div>
                ))}
              </div>
              <div className="flex justify-end">
                <Button onClick={() => setShowDependencyModal(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Available Updates Modal */}
      {showUpdatesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white shadow-xl">
            <CardHeader className="border-b border-gray-200">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold text-gray-900">
                  <Download className="h-5 w-5" />
                  Available Updates ({availableUpdates.length})
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowUpdatesModal(false);
                    setAvailableUpdates([]);
                  }}
                  className="h-8 w-8 p-0 hover:bg-gray-100"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-4">
                {availableUpdates.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">All Plugins Up to Date</h3>
                    <p className="text-gray-600">No updates available at this time.</p>
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                      <div className="flex items-start gap-2">
                        <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-blue-800">
                          <p className="font-medium mb-1">Update Information</p>
                          <p className="text-blue-700">
                            {availableUpdates.length} plugin{availableUpdates.length > 1 ? 's' : ''} have updates available. 
                            You can update them individually or all at once. Make sure to backup your system before updating.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {availableUpdates.map((update) => (
                        <Card key={update.id} className="border-l-4 border-l-blue-500">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <Package className="h-5 w-5 text-blue-600" />
                                  <h4 className="font-semibold text-gray-900">{update.name}</h4>
                                  <Badge variant="outline" className="text-orange-600 border-orange-600">
                                    Update Available
                                  </Badge>
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{update.description}</p>
                                
                                <div className="flex items-center gap-4 text-sm mb-3">
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Current:</span>
                                    <Badge variant="secondary">v{update.currentVersion}</Badge>
                                  </div>
                                  <span className="text-gray-400">→</span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-gray-500">Available:</span>
                                    <Badge variant="outline" className="text-green-600 border-green-600">
                                      v{update.availableVersion}
                                    </Badge>
                                  </div>
                                </div>

                                {update.updateNotes && update.updateNotes.length > 0 && (
                                  <div className="bg-gray-50 rounded-md p-3">
                                    <p className="text-xs font-medium text-gray-700 mb-1">What's New:</p>
                                    <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                                      {update.updateNotes.map((note, index) => (
                                        <li key={index}>{note}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setSelectedPlugin(plugins.find(p => p.id === update.id));
                                  setUpdateData({
                                    currentVersion: update.currentVersion,
                                    availableVersion: update.availableVersion,
                                    updateNotes: update.updateNotes
                                  });
                                  setShowUpdatesModal(false);
                                  setShowUpdateModal(true);
                                }}
                                className="ml-4 flex items-center gap-2"
                              >
                                <Download className="h-4 w-4" />
                                Update
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-6">
                      <div className="text-sm text-gray-600">
                        <p>Total updates available: <span className="font-medium text-gray-900">{availableUpdates.length}</span></p>
                      </div>
                      <div className="flex gap-3">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowUpdatesModal(false);
                            setAvailableUpdates([]);
                          }}
                          disabled={checkingUpdates}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleBulkUpdate}
                          disabled={checkingUpdates || availableUpdates.length === 0}
                          className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {checkingUpdates ? (
                            <>
                              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                              Updating...
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4 mr-2" />
                              Update All ({availableUpdates.length})
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InstalledPlugins;